<script setup lang="ts">
import {computed} from "vue";
import {useRoute, useRouter} from "vue-router";
import {useStorage} from "@vueuse/core";
import {saving} from "@/Composables/api";
import {getQuestion, storeQuestion, updateQuestion} from "@/Services/Admin/admin-questions";
import {Button, Card, Checkbox, InputText, Label, Select, Textarea} from "@/Components/Library";
import {PageHeading} from "@/Components/Global";
import {FormButtons, Messages} from "@/Components/Forms";
import {QuestionTypes} from "@/Enums";

const bag = "user-admin-form";
const currentRoute = useRoute();
const router = useRouter();

const hasOptions = computed(
    () => ["multiple_choice", "vote", "ranking", "slider", "weighting"].indexOf(question.value.type?.value) > -1,
);

const addOption = () => {
    question.value.options.push({value: ""});
};

const removeOption = (i) => {
    if (question.value.options.length === 1) {
        question.value.options[0] = {value: ""};
        return;
    }
    question.value.options.splice(i, 1);
};

const defaultForm = {
    id: null,
    title: null,
    description: null,
    instructions: null,
    allow_multiple: false,
    show_others_responses: false,
    type: null,
    options: [{value: ""}],
    tags: [],
};

const question = useStorage(bag, defaultForm);

if (currentRoute.params.question) {
    getQuestion(currentRoute.params.question).then(({data}) => {
        Object.assign(question.value, data);
        question.value.type = data.type.slug;
    });
} else if (question.value.id) {
    question.value = defaultForm;
}

const save = async () => {
    let postData = {
        id: question.value.id,
        title: question.value.title,
        description: question.value.description,
        instructions: question.value.instructions,
        allow_multiple: question.value.allow_multiple,
        show_others_responses: question.value.show_others_responses,
        type: question.value.type,
        options: question.value.options,
        tags: question.value.tags.map((tag) => tag.name),
    };

    if (currentRoute.params.question) {
        await updateQuestion(postData);
    } else {
        await storeQuestion(postData).then(({data}) => {
            Object.assign(question.value, data);
            question.value.type = data.type.slug;
        });
    }

    router.push({name: "admin.questions.show", params: {question: question.value.id}});
};
</script>
<template>
    <div class="mx-auto w-full max-w-5xl">
        <div class="m-container mb-3">
            <PageHeading :title="`${currentRoute.params.question ? 'Edit' : 'Create'} Question`" />
        </div>
        <form class="flex flex-col gap-4" @submit.prevent="save">
            <Card>
                <template #content>
                    <div class="flex flex-col gap-4">
                        <div class="flex flex-col">
                            <Label for="question" class="mb-1" required>Question</Label>
                            <Textarea
                                id="question"
                                name="question"
                                label="Question"
                                v-model="question.title"
                                class="w-full"
                                required
                                autoResize
                            />
                        </div>
                        <div class="flex flex-col">
                            <Label for="type" class="mb-1" required>Type</Label>
                            <Select
                                id="type"
                                name="type"
                                v-model="question.type"
                                :options="Object.values(QuestionTypes)"
                                option-value="slug"
                                option-label="name"
                                required
                                fluid
                            />
                        </div>
                        <div v-if="hasOptions">
                            <h3 class="mb-1 font-bold">Options</h3>
                            <div class="mb-2 flex flex-col gap-1">
                                <template v-for="(option, i) in question.options" :key="i">
                                    <div class="flex items-center">
                                        <InputText
                                            class="w-full"
                                            :placeholder="`Option ${i + 1}`"
                                            v-model="question.options[i].value"
                                        />
                                        <Button
                                            class="ml-1 h-8 w-8 bg-red-500 text-white"
                                            icon="mdi:minus"
                                            @click="removeOption(i)"
                                        />
                                    </div>
                                </template>
                            </div>
                            <Button
                                class="w-full rounded-md border border-askr-green bg-white px-4 py-2 text-center font-bold text-askr-green"
                                @click="addOption"
                            >
                                Add option
                            </Button>
                        </div>
                        <div>
                            <div class="mb-2 text-sm font-bold">Parameters</div>
                            <div class="flex flex-col gap-2">
                                <div class="flex items-center gap-1">
                                    <Checkbox
                                        id="allow_multiple"
                                        name="allow_multiple"
                                        label="Allow multiple responses"
                                        v-model="question.params.allow_multiple"
                                        binary
                                    />
                                    <Label for="allow_multiple">Allow multiple responses</Label>
                                </div>
                                <div class="flex items-center gap-1">
                                    <Checkbox
                                        id="show_others_responses"
                                        name="show_others_responses"
                                        label="Show other users' responses"
                                        v-model="question.show_others_responses"
                                        binary
                                    />
                                    <Label for="show_others_responses">Show other users' responses</Label>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
            </Card>
            <!--            <Card class="lg:mb-4">-->
            <!--                <TagSelect v-model="question.tags" />-->
            <!--            </Card>-->
            <pre>{{ question }}</pre>
            <Messages :bag="bag" />
            <FormButtons :loading="!!saving" :show-cancel="false" />
        </form>
    </div>
</template>
<style scoped></style>
